import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ExplorePresentationProps } from '../ExploreNawyNowContainer';
import { SearchFiltersTypes } from '../../../../../reducers/search-filters/reducer';
import AutoColumnsGrid from '../../../../../new-components/display-system/AutoColumnsGrid';
import NawyLoadingIndicator from '../../../../../new-components/NawyLoadingIndicator';
import MapSearchResultsToCards from '../../../../search/components/search-results/MapSearchResultsToCards';
import LoadMoreContainer from '../../../../../new-components/load-more/LoadMoreContainer';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  SearchContext,
  SearchContextInterface
} from '../../../../search-new/context/SearchContext';
import { resetFilters } from '../../../../../helpers/search-filters';
import MobileModalFiltersView from '../../../../search/components/filters/mobile/MobileModalFiltersView';
import { ModalShowTypeState } from '../../../../search/components/filters/mobile/MobileFilterBarPresentation';
import ErrorComponent from '../../../../../new-components/ui/ErrorComponent';
import ResetFilters from '../../../../entity/components/explore-properties/ResetFilters';
import useExploreSearch from '../../../hooks/NawyNowResults';

const MExploreNawyNowPresentation = ({
  entityDetails,
  loadMoreHandler,
  explorePropertiesRef,
  exploreLocation,
  pageNumber,
  activeTab,
  initialSearchResults
}: ExplorePresentationProps) => {
  const [loadedData, setLoadedData] = useState<any>();
  const { searchFiltersState, searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const [showModal, setShowModal] = useState<ModalShowTypeState>({
    show: false,
    type: SearchFiltersTypes.SORT_BY
  });
  const searchResults = useExploreSearch(
    searchFiltersState.searchBody,
    pageNumber,
    exploreLocation,
    {
      data: {
        values: initialSearchResults?.values,
        total_properties: initialSearchResults?.total_properties
      }
    }
  );
  const router = useRouter();
  useEffect(() => {
      if (searchResults.loadedSearchResults) {
        if (pageNumber == 1 || pageNumber == undefined) {
          // Add the newly loaded search results to the existing ones
          setLoadedData([...searchResults.loadedSearchResults]);
        } else {
          setLoadedData([...loadedData, ...searchResults.loadedSearchResults]);
          // Set the newly loaded search results as the loadedData state
        }
      }
  }, [searchResults.loadedSearchResults]);

  const getInfo = () => {
    return {
      results: searchResults.loadedSearchResults,
      properties_count: searchResults.loadedSearchResultsCount,
      compounds_count: searchResults.loadedSearchResultsCount,
      isLoading: searchResults.isLoading,
      isError: searchResults.isError
    };
  };

  const closeModal = () => {
    setShowModal({
      ...showModal,
      show: false
    });
  };
  const getCardsContainer = () => {
    const { results, isLoading, isError } = getInfo();
    if (isError) return <ErrorComponent />;
    else if (isLoading && pageNumber == 1) {
      return <NawyLoadingIndicator />;
    } else if (results && results?.length > 0) {
      return (
        <AutoColumnsGrid
          minWidth={'800px'}
          rowsGap={'16px'}
          columnsGap={'16px'}
        >
          <MapSearchResultsToCards
            activeTab={activeTab}
            searchResults={loadedData}
            findType={'property'}
            searchLocation={exploreLocation}
          />
        </AutoColumnsGrid>
      );
    } else {
      if (entityDetails?.type === SearchFiltersTypes.COMPOUNDS) {
        return <ResetFilters />;
      }
      const NoResultsIndicator = dynamic(
        () => import('../../../../../new-components/NoResultsIndicator')
      );
      return (
        <NoResultsIndicator
          onClick={() => {
            resetFilters(router, searchFiltersDispatch);
          }}
          isResetButton={true}
        />
      );
    }
  };

  const getLoadMoreContainer = () => {
    const { compounds_count, properties_count, isLoading } = getInfo();
    return (
      <LoadMoreContainer
        loadedDataLength={loadedData?.length}
        limit={
          searchFiltersState.searchFilters.category === 'property'
            ? properties_count
            : compounds_count
        }
        isLoading={isLoading}
        pageNumber={pageNumber}
        onChange={() => loadMoreHandler && loadMoreHandler(pageNumber + 1)}
      />
    );
  };

  return (
    <>
      <ExploreContainer>
        <div className="cards-wrapper" ref={explorePropertiesRef}>
          {getCardsContainer()}
        </div>
        {getLoadMoreContainer()}
      </ExploreContainer>
      {showModal.show && (
        <MobileModalFiltersView
          modalShowType={showModal}
          closeModal={closeModal}
        />
      )}
    </>
  );
};

const ExploreContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;

    .sort-btn {
      border-radius: 10px;
      padding: 5px 10px;
    }

    .results-title {
      display: flex;
      max-width: calc(100% - 164px);
      gap: 4px;
      flex-direction: column;

      .entity-title {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        margin-bottom: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .headline-subtitle {
        font-size: 12px;
        color: #656565;
      }
    }
  }

  .title-actions {
    display: flex;
    gap: 8px;
  }

  .cards-wrapper {
    position: relative;
    min-height: 400px;
    margin-top: 16px;
  }

  .load-more {
    margin-top: 16px;

    button {
      border: 0 solid transparent;
      padding: 0px 20px;
      margin: 0;

      span {
        color: #015c9a;
        text-align: center;
        font-family: materialMedium;
        font-size: 16px;
        display: flex;
        align-items: center;
        gap: 4px;

        svg {
          width: 20px;
          height: 20px;
          transform: rotate(90deg);
        }
      }
    }
  }

  .explore-title {
    display: flex;
    align-items: center;
    padding: 4px 0px;
    .entity-title {
      margin-inline-end: 4px;
    }
  }
`;

const FilterBtn = styled.button`
  background-color: #015c9a;
  outline: none;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  color: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-family: materialMedium;
  line-height: 150%;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 10px;
    color: ${props => props.theme.light.colors.white};
    background-color: ${props => props.theme.light.colors.primaryOrange};
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`;

export default MExploreNawyNowPresentation;
